import { Link } from 'react-router-dom'
import { useOwnVendorEvents } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { formatEventDate } from '@/lib/eventTime'
import { Badge, StatusBadge } from '@/components/ui/badge'
import { LoadingState, ErrorState } from '@/components/ui/states'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Table, TableBody, TableCell, TableEmptyRow, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { VendorEventListItemDto } from '@lib/dto/vendor-event.dto'
import { vendorEventHref, vendorEventReasons, vendorEventStatus } from './vendorEvents'
import { ProfileChecklistCard } from '@/components/vendor-profile/ProfileChecklistCard'

/** The event's name, a link to it when there is somewhere to go (see `vendorEventHref`). */
function EventName({ event }: { event: VendorEventListItemDto }) {
  const href = vendorEventHref(event)
  if (href === null) return <span className="text-sm font-medium">{event.name}</span>
  return (
    <Link to={href} className="text-sm font-medium hover:underline">
      {event.name}
    </Link>
  )
}

function EventsTable({ heading, events, empty }: { heading: string; events: VendorEventListItemDto[]; empty: string }) {
  return (
    <Table heading={heading}>
      <TableHeader>
        <TableHead>Event</TableHead>
        <TableHead>Date</TableHead>
        <TableHead>Where</TableHead>
        <TableHead>Status</TableHead>
      </TableHeader>
      <TableBody>
        {events.length === 0 ? <TableEmptyRow colSpan={4}>{empty}</TableEmptyRow> : null}
        {events.map((event) => (
          <TableRow key={event.eventId}>
            <TableCell>
              <EventName event={event} />
              <p className="text-xs text-ink-muted">{vendorEventReasons(event.services, event.rents)}</p>
            </TableCell>
            <TableCell className="text-sm">{event.date ? formatEventDate(event.date) : 'Date not set'}</TableCell>
            <TableCell>
              <p className="text-sm">{event.venueName}</p>
              <p className="text-xs text-ink-muted">{event.organizationName}</p>
            </TableCell>
            <TableCell>
              {event.unavailable ? <Badge tone="neutral">Unavailable</Badge> : <StatusBadge status={vendorEventStatus(event)} />}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/**
 * Every event the company is on, at every venue and every organization it
 * works with, in one list (docs/planner-vendors.md). Only its own: never
 * another event at the same venue.
 *
 * A canceled event stays, marked, so nothing disappears without a word, and a
 * past one moves to Past.
 */
export function VendorEventsPage() {
  const query = useOwnVendorEvents()

  function body() {
    if (query.isPending) return <LoadingState label="Loading your events…" />
    if (query.isError) {
      return <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
    }
    const upcoming = query.data.events.filter((e) => !e.hasFinished)
    const past = query.data.events.filter((e) => e.hasFinished)
    if (query.data.events.length === 0) {
      return (
        <p className="py-8 text-center text-sm text-ink-muted">
          No events yet. When a venue adds your company to an event, or rents your furniture for one, it shows here.
        </p>
      )
    }
    return (
      <div className="space-y-6">
        <EventsTable heading="Upcoming" events={upcoming} empty="Nothing coming up." />
        {past.length > 0 ? <EventsTable heading="Past" events={past} empty="" /> : null}
      </div>
    )
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <PageHeader title="Events" description="Every event your company is on, at every venue you work with." />
      <ProfileChecklistCard />
      {body()}
    </PageContainer>
  )
}
